/**
 * ActiveReportsTab Component
 * Displays list of active (draft) power reports
 * Mobile cards / Desktop table with search and pagination
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { deleteReport, recalculateAllReports } from '../../../../services/oathPowerReportService';
import { OathButton, OathModal, OathEmptyState } from '../../../shared/OathUI';
import ReportCard from './ReportCard';
import { SortableHeader, Pagination, MobileLoadingSkeleton, TableLoadingSkeleton } from './shared';
import { formatDate, formatCurrencyWithSymbol } from '../utils/formatters';
import { PAGINATION_OPTIONS, SEARCH_DEBOUNCE_MS } from '../utils/constants';
import { useIsMobile } from '../utils/hooks';

const ActiveReportsTab = ({
  reports = [],
  isLoading = false,
  onRefresh,
  pagination = {},
  onPaginationChange,
  searchTerm = '',
  onSearchChange,
  agencyId
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [sortField, setSortField] = useState('dateCreated');
  const [sortDirection, setSortDirection] = useState('desc');
  const [refreshingRates, setRefreshingRates] = useState(false);

  // Local search state for debouncing
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const debounceTimerRef = useRef(null);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Debounced search handler
  const handleSearchInputChange = useCallback((value) => {
    setLocalSearchTerm(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(value);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [onSearchChange]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle view report
  const handleViewReport = (report) => {
    navigate(`/app/power-reports/${report.powerReportId}`);
  };

  // Handle delete
  const handleDelete = (report) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      const result = await deleteReport(reportToDelete.powerReportId);
      if (result.success) {
        toast.success(`Report ${reportToDelete.reportNumber} deleted successfully`);
        onRefresh();
      } else {
        toast.error('Failed to delete report: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      toast.error('Failed to delete report');
      console.error(err);
    } finally {
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
    }
  };

  // Handle refresh rates for all active reports
  const handleRefreshRates = async () => {
    if (!agencyId) {
      toast.error('Agency ID not available');
      return;
    }
    setRefreshingRates(true);
    try {
      const result = await recalculateAllReports(agencyId);
      if (result.success) {
        toast.success(result.data?.message || 'Reports refreshed successfully.');
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to refresh: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to refresh reports: ' + error.message);
      console.error('Refresh reports error:', error);
    } finally {
      setRefreshingRates(false);
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    onPaginationChange?.({ page: newPage, pageSize: pagination.pageSize });
  };

  const handlePageSizeChange = (newPageSize) => {
    onPaginationChange?.({ page: 0, pageSize: newPageSize });
  };

  // Sort reports locally (for display) - memoized to avoid re-sorting on every render
  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'dateCreated') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reports, sortField, sortDirection]);

  return (
    <>
      {/* Search Bar & Refresh Button */}
      <div className="mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-full sm:max-w-xs">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by report number..."
              value={localSearchTerm}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
            {localSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
          <div className="ml-auto">
            <button
              onClick={handleRefreshRates}
              disabled={refreshingRates}
              className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshingRates ? 'animate-spin' : ''}`} />
              {refreshingRates ? 'Refreshing...' : 'Refresh Reports from Server'}
            </button>
          </div>
        </div>
        {searchTerm && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Showing results for "{searchTerm}"
          </p>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        isMobile ? <MobileLoadingSkeleton /> : <TableLoadingSkeleton />
      ) : reports.length === 0 ? (
        <OathEmptyState
          icon={DocumentTextIcon}
          title={searchTerm ? 'No reports found' : 'No active reports'}
          message={searchTerm
            ? `No reports found matching "${searchTerm}". Try a different search term.`
            : 'Click "Create Report" to get started.'
          }
        />
      ) : isMobile ? (
        /* Mobile Card View */
        <div className="space-y-3">
          {sortedReports.map((report) => (
            <ReportCard
              key={report.powerReportId}
              report={report}
              onView={handleViewReport}
              onDelete={handleDelete}
              showDelete={true}
            />
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <SortableHeader label="Report #" field="reportNumber" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Date" field="dateCreated" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="# Bonds" field="powerCount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
                <SortableHeader label="Liability" field="totalBondAmount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                <SortableHeader label="Premium" field="totalPremium" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                <SortableHeader label="NET" field="totalNet" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                <SortableHeader label="BUF" field="totalBuf" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="right" />
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {sortedReports.map((report) => (
                <tr
                  key={report.powerReportId}
                  onClick={() => handleViewReport(report)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {report.reportNumber || 'New Report'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(report.dateCreated)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                    {report.powerCount || 0}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                    {formatCurrencyWithSymbol(report.totalBondAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                    {formatCurrencyWithSymbol(report.totalPremium)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-primary dark:text-primary-light text-right">
                    {formatCurrencyWithSymbol(report.totalNet)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                    {formatCurrencyWithSymbol(report.totalBuf)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(report); }}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"
                      aria-label="Delete report"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {reports.length > 0 && (
        <Pagination
          page={pagination.page || 0}
          pageSize={pagination.pageSize || PAGINATION_OPTIONS.PAGE_SIZE}
          totalCount={pagination.totalCount || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Delete Confirmation Modal */}
      <OathModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Confirm Delete"
        size="sm"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <OathButton variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </OathButton>
            <OathButton variant="danger" onClick={confirmDelete}>
              Delete Report
            </OathButton>
          </div>
        }
      >
        <p className="text-gray-700 dark:text-gray-300">
          Are you sure you want to delete report <strong>{reportToDelete?.reportNumber}</strong>?
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          This action cannot be undone. All powers in this report will be returned to available status.
        </p>
      </OathModal>
    </>
  );
};

export default ActiveReportsTab;
